import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getParentAvatar, getChildAvatar } from '../constants/avatars';
import { API_ENDPOINTS } from '../config/api';
import './ChatMessage.css';

function ChatMessage({ message, messages = [], onIllustrate }) {
  const { currentUser } = useAuth();
  const [isGenerating, setIsGenerating] = useState(false);
  const [illustration, setIllustration] = useState(message.illustration || null);
  const [illustrationFailed, setIllustrationFailed] = useState(false);

  // Find the original question if this is a parent update reply
  const originalQuestion = message.isParentUpdate && message.replyToQuestionId && messages.length > 0
    ? messages.find(msg => msg.id === message.replyToQuestionId)
    : null;

  const isMathProblem = (question) => {
    if (!question) return false;
    const lowerQuestion = question.toLowerCase();
    
    // Check for numbers (digits)
    const hasNumbers = /\d/.test(question);
    
    // Check for number words
    const numberWords = [
      'zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten',
      'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen', 'twenty',
      'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety', 'hundred', 'thousand', 'million',
      'first', 'second', 'third', 'fourth', 'fifth', 'sixth', 'seventh', 'eighth', 'ninth', 'tenth'
    ];
    const hasNumberWords = numberWords.some(word => lowerQuestion.includes(word));
    
    return hasNumbers || hasNumberWords;
  };

  const isWhoOrWhatQuestion = (question) => {
    if (!question) return false;
    const trimmedQuestion = question.trim();
    const lowerQuestion = trimmedQuestion.toLowerCase();
    return lowerQuestion.startsWith('who') || lowerQuestion.startsWith('what');
  };

  const handleIllustrate = async () => {
    if (isGenerating || illustration || illustrationFailed) return;
    
    setIsGenerating(true);
    try {
      const response = await fetch(API_ENDPOINTS.ILLUSTRATE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          answerText: message.text,
          question: message.question || ''
        }),
      });

      // 204 No Content means empty response - fail silently and hide button
      if (response.status === 204) {
        setIllustrationFailed(true);
        return;
      }

      if (!response.ok) {
        setIllustrationFailed(true);
        return;
      }

      const data = await response.json();
      setIllustration(data.image);
      
      // Notify parent component to update the message
      if (onIllustrate) {
        onIllustrate(message.id, data.image);
      }
    } catch (error) {
      console.error('Error generating illustration:', error);
      // Silently fail and hide button
      setIllustrationFailed(true);
    } finally {
      setIsGenerating(false);
    }
  };

  // Determine avatar source
  const getAvatarSrc = () => {
    if (message.isBot || message.isParentUpdate) {
      // Parent/bot messages use parent avatar
      return getParentAvatar(currentUser);
    } else {
      // Child messages use child avatar
      return getChildAvatar();
    }
  };

  const avatarSrc = getAvatarSrc();
  const avatarAlt = message.isBot || message.isParentUpdate 
    ? (currentUser?.displayName || 'Parent')
    : 'Child';

  return (
    <div className={`message ${message.isBot || message.isParentUpdate ? 'bot-message' : 'user-message'} ${message.isParentUpdate ? 'parent-update-message' : ''}`}>
      <div className="avatar">
        <div className="avatar-circle">
          <img src={avatarSrc} alt={avatarAlt} className="avatar-image" />
        </div>
      </div>
      <div className="message-content">
        {message.isParentUpdate && originalQuestion && (
          <div className="message-reply-quote">
            <div className="reply-quote-content">
              <span className="reply-quote-label">You asked:</span>
              <span className="reply-quote-text">{originalQuestion.text}</span>
            </div>
          </div>
        )}
        <div className={message.isParentUpdate ? "message-text parent-update-text" : "message-text"}>
          {message.isParentUpdate ? (
            <>
              <strong>Parent Update: </strong>{message.text}
            </>
          ) : (
            message.text
          )}
        </div>
        {message.isBot && !message.isParentUpdate && message.question && !illustration && !illustrationFailed && !isMathProblem(message.question) && isWhoOrWhatQuestion(message.question) && (
          <button 
            className="illustrate-button"
            onClick={handleIllustrate}
            disabled={isGenerating}
          >
            {isGenerating ? '🎨 Generating...' : '🎨 Illustrate'}
          </button>
        )}
        {illustration && (
          <div className="illustration-container">
            <img 
              src={illustration} 
              alt="Illustration" 
              className="illustration-image"
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default ChatMessage;

